import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { GET_SECTION_DATA } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { GetSectionDataResponse, Section } from "../types/mmp-graphql";

import { useReactQueryErrorReporter } from "./use-error-reporter";
import { useMyEnrollments } from "./useMyEnrollments";

export const useGetMySections = (): {
  sections: Section[];
  isLoading: boolean;
  refetch: () => void;
} => {
  // The coach's section ids come from their enrollments (the user's own record
  // path no longer carries section ids). We still fetch the full Section
  // records by id so consumers keep every field (path, coachSubscription, etc.)
  // that the enrollment payload doesn't carry.
  const { enrollments, loading: enrollmentsLoading } = useMyEnrollments();

  const sectionIds = useMemo(
    () => [
      ...new Set(
        enrollments
          .filter((enrollment) => enrollment.scope === "SECTION")
          .map((enrollment) => enrollment.entityId),
      ),
    ],
    [enrollments],
  );

  const { data, error, isLoading, refetch } = useQuery<
    GetSectionDataResponse[]
  >({
    queryKey: ["mySection", sectionIds],
    queryFn: () =>
      Promise.all(
        sectionIds.map((sectionId) =>
          graphQLClient.request<GetSectionDataResponse>(GET_SECTION_DATA, {
            id: sectionId,
          }),
        ),
      ),
    enabled: sectionIds.length > 0,
    staleTime: 1000 * 60 * 60 * 2, // Consider data fresh for 2 hours, this rarely changes
    gcTime: 1000 * 60 * 60 * 72, // Keep unused data in cache for 72 hours
  });

  useReactQueryErrorReporter("Could not find section", error, false);

  // Default to [] (never undefined) so callers can't accidentally render an
  // empty state as "no sections" mid-load; use isLoading to distinguish that.
  const sections = data?.map((section) => section.getSectionById) ?? [];

  return {
    sections,
    isLoading: isLoading || enrollmentsLoading,
    refetch,
  };
};
